'use client';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { RANKINGS_TYPE_OPTIONS } from '@/modules/rankings/constants';
import { MobileFilter } from '@/modules/rankings/MobileFilter/MobileFilter';
import { PlayersFilter } from '@/modules/rankings/PlayersFilter';
import { getDefaultFilters } from '@/modules/rankings/rankingsUtils';

interface FiltersRankingsWrapperProps {
  canExportRankings: boolean;
}

export const FiltersRankingsWrapper = ({ canExportRankings }: FiltersRankingsWrapperProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const filtersFromUrl = useMemo(() => getDefaultFilters(searchParams), [searchParams]);
  const [filters, setFilters] = useState(filtersFromUrl);

  useEffect(() => {
    setFilters(filtersFromUrl);
  }, [filtersFromUrl]);

  const handleRankingType = (rankingType: string) => {
    const newSearchParams = new URLSearchParams(searchParams || '');
    newSearchParams.set('ranking_type', rankingType);
    newSearchParams.set('page', '1');
    newSearchParams.set('gender', 'F');
    newSearchParams.set('type', rankingType === 'proWorldRanking' ? '8' : '1');
    router.replace(`?${newSearchParams.toString()}`);
  };

  return (
    <>
      <div className='bg-white py-3 sm:py-4'>
        <div className='mx-auto flex flex-col max-w-screen-xl px-3 sm:px-4 2xl:px-0'>
          <div className='hidden h-14 w-full gap-x-2 rounded-lg bg-purple-50 p-2 sm:flex'>
            {RANKINGS_TYPE_OPTIONS.map((option) => (
              <button
                key={`rankings-type-button-${option.value}`}
                className={clsx(
                  'flex !w-1/2 items-center justify-center rounded-lg py-3 transition-colors font-medium',
                  filters.rankingType === option.value
                    ? 'bg-experiment text-white'
                    : 'bg-background text-gray-700 hover:bg-experiment hover:text-white'
                )}
                onClick={() => handleRankingType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className='flex size-full items-center justify-center gap-x-3 sm:hidden'>
            {RANKINGS_TYPE_OPTIONS.map((option) => (
              <button
                key={`rankings-type-mobile-button-${option.value}`}
                className={clsx(
                  'flex h-10 !w-1/2 items-center justify-center whitespace-nowrap px-2 text-sm font-medium transition-colors rounded-full border',
                  filters.rankingType === option.value
                    ? 'bg-experiment text-white border-transparent'
                    : 'bg-background text-gray-700 border-gray-300 hover:bg-experiment hover:text-white hover:border-experiment'
                )}
                onClick={() => handleRankingType(option.value)}
              >
                {option.shortLabel}
              </button>
            ))}
          </div>
        </div>
      </div>
      <section className='hidden w-full flex-col items-center justify-center bg-white sm:flex'>
        <PlayersFilter
          filters={filters}
          setFilters={setFilters}
          canExportRankings={canExportRankings}
        />
      </section>
      <section className='flex w-full flex-col justify-center overflow-x-hidden sm:hidden'>
        <MobileFilter
          filters={filters}
          setFilters={setFilters}
          canExportRankings={canExportRankings}
        />
      </section>
    </>
  );
};
